import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CodeBlock } from '@/components/ui/code-block';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  dockerComposeFile,
  dockerLoginCommand,
  dockerRunCommand,
  fetchGatewayDeploymentInfo,
  k8sDeployment,
  k8sPullSecretCommand,
} from '@/features/gateway-deploy';

interface DeployToken {
  id: string;
  token: string;
  name: string | null;
  gatewayType: string;
  status: string;
}

interface Props {
  /** All provisioning tokens for the org; the picker prefers docker-type ones. */
  tokens: DeployToken[];
  /** ISO/OVA download UI, rendered under the "Virtual machine" tab. */
  virtualMachineSlot?: ReactNode;
  /**
   * Use this exact token and hide the picker.
   *
   * The Add gateway flow mints a token as part of the path the user already
   * chose, so there is nothing to pick — asking them to select the token they
   * just caused to exist is the "create one above, then come back here" loop
   * this flow exists to remove.
   */
  token?: string;
}

/** What a Docker host needs beyond the commands for the technician VPN. */
function VpnHostNote() {
  return (
    <p className="text-xs text-muted-foreground">
      For the technician VPN, the host also needs WireGuard in its kernel, as Ubuntu 24.04, Debian 12 and
      Rocky Linux 9 have.{' '}
      <a
        className="underline"
        href="https://witinaai.com/docs/vpn/docker-gateways/"
        target="_blank"
        rel="noreferrer"
      >
        What a Docker host needs for the VPN
      </a>
    </p>
  );
}

/**
 * "Deploy a gateway" method picker: Docker, Docker Compose, Kubernetes, and
 * Virtual machine (ISO/OVA). The provisioning token doubles as the registry
 * pull password (`docker login -u gateway-token`) for the container tabs. The
 * container tabs need a configured registry (backend 503s on the trial
 * edition → they're omitted); the VM tab shows whenever a slot is provided.
 */
export function DockerDeployInstructions({ tokens, virtualMachineSlot, token: fixedToken }: Props) {
  const { data: info, isLoading } = useQuery({
    queryKey: ['gateway-deployment-info'],
    queryFn: fetchGatewayDeploymentInfo,
    staleTime: Infinity,
    retry: false,
  });

  // Prefer docker-type tokens (minted with a long expiry so they keep pulling),
  // but allow any non-cancelled token so the picker is never empty.
  const usableTokens = useMemo(
    () => tokens.filter((t) => t.status !== 'cancelled' && t.status !== 'failed'),
    [tokens],
  );
  const dockerTokens = usableTokens.filter((t) => t.gatewayType === 'docker');
  const pickable = dockerTokens.length > 0 ? dockerTokens : usableTokens;

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [namespace, setNamespace] = useState('default');

  const hasContainer = !!info;
  const hasVm = !!virtualMachineSlot;
  const defaultTab = hasVm ? 'vm' : 'docker';
  const [activeTab, setActiveTab] = useState(defaultTab);

  if (isLoading) return null;
  if (!hasContainer && !hasVm) return null;

  const token = pickable.find((t) => t.id === (selectedId ?? pickable[0]?.id));
  // Honest placeholder rather than an instruction: the token that used to be
  // "above" now comes from the Add gateway flow, which fills it in for you.
  const tokenValue = fixedToken ?? token?.token ?? '<your provisioning token>';
  const showTokenPicker = !fixedToken && hasContainer && activeTab !== 'vm';

  const tokenLabel = (t: DeployToken) =>
    `${t.name ?? 'Unnamed'} — ${t.token.slice(0, 6)}…${t.gatewayType === 'docker' ? '' : ` (${t.gatewayType})`}`;

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {hasVm ? <TabsTrigger value="vm">Virtual machine</TabsTrigger> : null}
          {hasContainer ? (
            <>
              <TabsTrigger value="docker">Docker</TabsTrigger>
              <TabsTrigger value="compose">Docker Compose</TabsTrigger>
              <TabsTrigger value="k8s">Kubernetes</TabsTrigger>
            </>
          ) : null}
        </TabsList>

        {showTokenPicker ? (
          <div className="mt-3 space-y-1">
            <Label htmlFor="deploy-token">Provisioning token</Label>
            {pickable.length > 0 ? (
              <>
                <Select value={token ? token.id : undefined} onValueChange={setSelectedId}>
                  <SelectTrigger id="deploy-token" className="max-w-sm">
                    <SelectValue placeholder="Select a token" />
                  </SelectTrigger>
                  <SelectContent>
                    {pickable.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {tokenLabel(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Pulls the image from{' '}
                  <code className="font-mono text-xs">{info.registry}</code>; the token is
                  also the registry password — no separate credential needed.
                  {dockerTokens.length === 0
                    ? ' Tip: Add gateway → Run it as a container issues a long-lived token, so Kubernetes can keep re-pulling the image.'
                    : ''}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No provisioning tokens yet. Use <strong>Add gateway</strong> → “Run it as
                a container” and these commands come back filled in.
              </p>
            )}
          </div>
        ) : null}

        {hasContainer ? (
          <>
            <TabsContent value="docker" className="space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">1. Log in to the registry</p>
                <CodeBlock code={dockerLoginCommand(info, tokenValue)} />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">2. Run the gateway</p>
                <CodeBlock code={dockerRunCommand(info, tokenValue)} />
              </div>
              <VpnHostNote />
            </TabsContent>

            <TabsContent value="compose" className="space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">1. Log in to the registry</p>
                <p className="text-xs text-muted-foreground">
                  Compose reuses the credentials this stores in{' '}
                  <code className="font-mono text-xs">~/.docker/config.json</code>.
                </p>
                <CodeBlock code={dockerLoginCommand(info, tokenValue)} />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">2. Save and run the compose file</p>
                <CodeBlock title="docker-compose.yml" code={dockerComposeFile(info, tokenValue)} maxHeight="20rem" />
              </div>
              <VpnHostNote />
            </TabsContent>

            <TabsContent value="k8s" className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="deploy-namespace">Namespace</Label>
                <Input
                  id="deploy-namespace"
                  value={namespace}
                  onChange={(e) => { setNamespace(e.target.value || 'default'); }}
                  className="max-w-xs"
                />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">1. Create the image-pull secret</p>
                <p className="text-xs text-muted-foreground">
                  Kubernetes re-pulls on every reschedule, so it needs its own registry
                  secret (it can’t reuse a host login).
                </p>
                <CodeBlock code={k8sPullSecretCommand(info, tokenValue, namespace)} />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">2. Apply the Deployment</p>
                <CodeBlock title="gateway-deployment.yaml" code={k8sDeployment(info, tokenValue, namespace)} maxHeight="24rem" />
              </div>
              <p className="text-xs text-muted-foreground">Gateways on Kubernetes don&rsquo;t run the technician VPN yet.</p>
            </TabsContent>
          </>
        ) : null}

        {hasVm ? <TabsContent value="vm">{virtualMachineSlot}</TabsContent> : null}
      </Tabs>
    </div>
  );
}
